import React, { useState } from 'react';
import { FaBoxOpen, FaCameraRetro, FaEnvelopeOpenText, FaLayerGroup, FaLock, FaRegHeart, FaRegImage } from 'react-icons/fa';
import {
  GIFT_FORMATS,
  GIFT_FORMAT_OPTIONS,
  getGiftFormatForOpeningMode,
} from '../services/giftBuilderConfig';

const FORMAT_ICONS = {
  layer_by_layer: FaLayerGroup,
  memory_page: FaRegHeart,
  gift_reveal: FaBoxOpen,
  photo_journey: FaCameraRetro,
  letters_for_later: FaEnvelopeOpenText,
  keepsake_moment: FaRegImage,
  surprise_reveal: FaLock,
};

const GiftFormatSelector = ({ pageData, setPageData, theme }) => {
  const [isChoosing, setIsChoosing] = useState(false);
  const selectedFormat = pageData.giftFormat || getGiftFormatForOpeningMode(pageData.openingMode);
  const selected = GIFT_FORMATS[selectedFormat] || GIFT_FORMATS.layer_by_layer;
  const SelectedIcon = FORMAT_ICONS[selectedFormat] || FaRegHeart;

  return (
    <div className="lumirae-editor-panel p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color: theme.accent }}>
            Choose the presentation
          </div>
          <p className="mt-2 text-xs font-medium leading-relaxed opacity-60">
            Pick how the gift should open. Guided Layers is recommended because every module gets its own reveal.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsChoosing((value) => !value)}
          className="lumirae-ds-button lumirae-ds-button-secondary min-h-0 px-4 py-2 text-[10px] font-semibold normal-case tracking-normal"
        >
          {isChoosing ? 'Hide Styles' : 'Change Style'}
        </button>
      </div>

      <div className="mt-4 rounded-[1.25rem] border p-4" style={{ borderColor: theme.accent, backgroundColor: theme.accentSoft }}>
        <div className="flex items-start gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[1.15rem] border text-sm shadow-sm" style={{ backgroundColor: theme.cardBg, borderColor: theme.cardBorder, color: theme.accent }}>
            <SelectedIcon />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold tracking-tight" style={{ color: theme.text }}>
              {selected.label}
            </div>
            <p className="mt-1 text-xs font-medium leading-relaxed opacity-70">
              {selected.description}
            </p>
          </div>
        </div>
      </div>

      {isChoosing && (
      <div className="mt-4 grid grid-cols-1 gap-2">
        {GIFT_FORMAT_OPTIONS.map((format) => {
          const active = format.key === selectedFormat;
          const Icon = FORMAT_ICONS[format.key] || FaRegHeart;
          return (
            <button
              key={format.key}
              type="button"
              onClick={() => {
                setPageData((prev) => ({
                  ...prev,
                  giftFormat: format.key,
                  openingMode: GIFT_FORMATS[format.key]?.openingMode || 'layered_gift',
                  openingStyleTouched: true,
                }));
                setIsChoosing(false);
              }}
              className="lumirae-format-card lumirae-ds-list-card flex items-start gap-3 p-3 text-left transition-all"
              style={{
                borderColor: active ? 'var(--lumirae-accent-primary)' : 'var(--lumirae-border-default)',
                boxShadow: active ? '0 16px 36px rgba(33, 27, 29, 0.09)' : '0 10px 24px rgba(15, 23, 42, 0.05)',
                transform: active ? 'translateY(-1px)' : 'none',
              }}
            >
              <span
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[1.25rem] border text-sm shadow-sm"
                style={{
                  backgroundColor: 'var(--lumirae-surface-raised)',
                  borderColor: active ? 'var(--lumirae-accent-primary)' : 'var(--lumirae-border-default)',
                  color: active ? 'var(--lumirae-accent-primary)' : 'var(--lumirae-text-secondary)',
                }}
              >
                <Icon />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-3">
                  <span className="block text-xs font-black uppercase tracking-wide" style={{ color: theme.text }}>
                    {format.label}
                  </span>
                  {(format.recommended || active) && (
                    <span className="text-[8px] font-black uppercase tracking-widest" style={{ color: theme.accent }}>
                      {active ? 'Selected' : 'Recommended'}
                    </span>
                  )}
                </span>
                <span className="mt-1 block text-[10px] font-medium leading-relaxed opacity-60">
                  {format.description}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      )}

      <div className="mt-4 rounded-[1.5rem] bg-white/60 px-4 py-3">
        <div className="lumirae-ds-meta">
          Best for
        </div>
        <p className="mt-1 text-[10px] font-black uppercase tracking-widest opacity-60">
          {selected.bestFor}
        </p>
      </div>
    </div>
  );
};

export default GiftFormatSelector;
